import React, { useState, useEffect, useCallback, useRef } from 'react';
import { boardsAPI } from '../../services/api';
import './boards.css';

// Job Nimbus-style boards list: a grid of named, colored board cards. Create,
// rename, recolor, delete. Clicking a card opens the board (Kanban) via onOpenBoard.

const BOARD_COLORS = ['#4a6cf7', '#16a34a', '#dc2626', '#d97706', '#0891b2', '#8b5cf6', '#db2777', '#0d9488', '#6b7280'];
const TYPE_LABEL = { Jobs: 'Jobs', Contacts: 'Contacts', WorkOrders: 'Additional Work' };

const BoardsList = ({ onOpenBoard }) => {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const menuRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await boardsAPI.getAll();
      setBoards(res.data || []);
    } catch (err) {
      console.error('Failed to load boards:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuFor(null); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleDelete = async (board) => {
    setMenuFor(null);
    if (!window.confirm(`Delete the board "${board.name}"? This removes the board and its columns — the ${TYPE_LABEL[board.type]?.toLowerCase() || 'records'} themselves are not deleted.`)) return;
    try {
      await boardsAPI.delete(board.id);
      setBoards((prev) => prev.filter((b) => b.id !== board.id));
    } catch (err) {
      alert('Could not delete that board.');
    }
  };

  const handleRename = async (board) => {
    setMenuFor(null);
    const name = window.prompt('Rename board:', board.name);
    if (!name || !name.trim()) return;
    try {
      const res = await boardsAPI.update(board.id, { name: name.trim() });
      setBoards((prev) => prev.map((b) => (b.id === board.id ? { ...b, name: res.data.name } : b)));
    } catch {
      alert('Could not rename that board.');
    }
  };

  const handleRecolor = async (board, color) => {
    setMenuFor(null);
    try {
      await boardsAPI.update(board.id, { color });
      setBoards((prev) => prev.map((b) => (b.id === board.id ? { ...b, color } : b)));
    } catch {
      alert('Could not update the color.');
    }
  };

  const filtered = boards.filter((b) => b.name.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="boards-page">
      <div className="boards-header">
        <h2>Boards</h2>
        <button className="boards-create-btn" onClick={() => setShowCreate(true)}>
          <i className="fas fa-plus"></i> Create
        </button>
      </div>

      <div className="boards-search">
        <i className="fas fa-search"></i>
        <input
          type="text"
          placeholder="Search boards..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="boards-empty">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="boards-empty">
          {boards.length === 0 ? 'No boards yet — click Create to make your first one.' : 'No boards match your search.'}
        </div>
      ) : (
        <div className="boards-grid">
          {filtered.map((board) => (
            <div key={board.id} className="board-tile" onClick={() => onOpenBoard(board.id)}>
              <span className="board-tile-bar" style={{ background: board.color || '#6b7280' }} />
              <div className="board-tile-body">
                <div className="board-tile-name">{board.name}</div>
                <div className="board-tile-count">{TYPE_LABEL[board.type] || board.type} Count: {board.count}</div>
              </div>
              <div className="board-tile-menu-wrap" ref={menuFor === board.id ? menuRef : null}>
                <button
                  className="board-tile-menu-btn"
                  onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === board.id ? null : board.id); }}
                >
                  <i className="fas fa-ellipsis-v"></i>
                </button>
                {menuFor === board.id && (
                  <div className="board-tile-menu" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => handleRename(board)}><i className="fas fa-pen"></i> Rename</button>
                    <div className="board-menu-colors">
                      {BOARD_COLORS.map((c) => (
                        <span
                          key={c}
                          className="board-color-dot"
                          style={{ background: c }}
                          onClick={() => handleRecolor(board, c)}
                        />
                      ))}
                    </div>
                    <button className="danger" onClick={() => handleDelete(board)}><i className="fas fa-trash"></i> Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateBoardModal
          onClose={() => setShowCreate(false)}
          onCreated={(board) => { setShowCreate(false); setBoards((prev) => [...prev, board]); onOpenBoard(board.id); }}
        />
      )}
    </div>
  );
};

const CreateBoardModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('Jobs');
  const [color, setColor] = useState(BOARD_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!name.trim()) { setError('Give the board a name.'); return; }
    setSaving(true);
    try {
      const res = await boardsAPI.create({ name: name.trim(), type, color });
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create the board.');
      setSaving(false);
    }
  };

  return (
    // Closing is Cancel only — no click-to-close on the backdrop.
    <div className="board-modal-overlay">
      <div className="board-modal">
        <h3>Create board</h3>
        <label className="board-modal-label">Name</label>
        <input className="board-modal-input" value={name} autoFocus onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()} placeholder="e.g. Sales, Production, Residential Insurance" />

        <label className="board-modal-label">Type</label>
        <div className="board-type-picker">
          {['Jobs', 'Contacts', 'WorkOrders'].map((t) => (
            <button key={t} className={`board-type-btn${type === t ? ' active' : ''}`} onClick={() => setType(t)}>
              {TYPE_LABEL[t]}
            </button>
          ))}
        </div>

        <label className="board-modal-label">Color</label>
        <div className="board-modal-colors">
          {BOARD_COLORS.map((c) => (
            <span key={c} className={`board-color-dot${color === c ? ' selected' : ''}`} style={{ background: c }} onClick={() => setColor(c)} />
          ))}
        </div>

        {error && <div className="board-modal-error">{error}</div>}

        <div className="board-modal-actions">
          <button className="board-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="board-modal-save" onClick={submit} disabled={saving}>{saving ? 'Creating…' : 'Create board'}</button>
        </div>
      </div>
    </div>
  );
};

export default BoardsList;
